import React from 'react';
import { FiClock, FiBookOpen, FiUsers, FiBarChart2, FiShield, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';

const TeacherWorkload = () => {
  const tasks = [
    { 
      icon: <FiClock className="w-6 h-6" />, 
      title: 'Attendance Management', 
      stat: '120+ hours annually',
      description: 'Every morning, teachers spend 10-15 minutes taking attendance and another 10 minutes marking it in registers. Multiply this by 200 school days and you have 83 hours just for roll calls. Add record updates and you reach 120+ hours—the equivalent of 3 weeks of teaching time.',
      solution: 'Truyon automates attendance with one-click marking and auto-sync. Teachers can mark attendance in 30 seconds, and the system automatically updates records and notifies parents.',
      color: 'bg-indigo-50' 
    },
    { 
      icon: <FiBookOpen className="w-6 h-6" />, 
      title: 'Grading & Assessment', 
      stat: '200+ hours annually',
      description: 'Grading assignments, calculating semester marks, and generating report cards is a massive time sink. Teachers spend hours checking calculations, formatting grade sheets, and ensuring accuracy. With 100+ students, this becomes a monumental task.',
      solution: 'Truyon automates grade calculation and report generation instantly. Teachers simply input marks, and the system calculates totals, percentages, grades, and generates professional report cards in seconds.',
      color: 'bg-emerald-50' 
    },
    { 
      icon: <FiUsers className="w-6 h-6" />, 
      title: 'Parent Communication', 
      stat: '80+ hours annually',
      description: 'Drafting individual messages, emails, or SMS updates to parents about student progress, events, attendance, or alerts takes 1-2 hours weekly. Teachers often do this after school hours, cutting into their personal time.',
      solution: 'Truyon automates parent communication with bulk messaging and alerts. Teachers send one message, and the system delivers it to all relevant parents via SMS, email, or app notification.',
      color: 'bg-amber-50' 
    },
    { 
      icon: <FiBarChart2 className="w-6 h-6" />, 
      title: 'Lesson Planning & Timetable', 
      stat: '60+ hours annually',
      description: 'Creating and updating lesson plans, adjusting timetables, and coordinating with other teachers creates another 1-2 hours of weekly work. Changes to schedules are often manual and chaotic, causing confusion and wasted time.',
      solution: 'Truyon provides automated scheduling and collaborative planning tools. Teachers can create lesson plans, share resources, and coordinate schedules seamlessly.',
      color: 'bg-rose-50' 
    },
    { 
      icon: <FiShield className="w-6 h-6" />, 
      title: 'Data Entry & Record Keeping', 
      stat: '80% report as top frustration',
      description: 'Entering marks, attendance, student details into multiple systems—Excel, school software, physical registers—creates repetitive and tedious data entry work. Teachers report this as their top frustration with administrative work.',
      solution: 'Truyon eliminates duplicate data entry with single-point input. Enter data once, and it automatically populates everywhere it\'s needed—attendance, grades, reports, and parent communication.',
      color: 'bg-purple-50' 
    },
    { 
      icon: <FiAlertTriangle className="w-6 h-6" />, 
      title: 'Student Progress Tracking', 
      stat: '40+ hours annually',
      description: 'Monitoring each student\'s progress, identifying those who need extra support, and updating progress reports takes significant time. Teachers manually track scores, identify struggling students, and create intervention plans.',
      solution: 'Truyon provides real-time analytics and automatic progress alerts. Teachers instantly see which students need attention, and can take action immediately.',
      color: 'bg-blue-50' 
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Every day, across India, talented teachers and professors spend hours on tasks that have nothing to do with teaching. 
        They mark attendance, calculate grades, communicate with parents, create lesson plans, and update records—administrative work 
        that steals precious time from their students.
      </p>
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Research shows that <strong className="text-slate-900">teachers spend an average of 8+ hours every week on administrative tasks</strong>—that's 
        nearly one full working day lost to paperwork. Over a year, this adds up to over 400 hours—the equivalent of 10 full working weeks 
        of teaching time lost.
      </p>
      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's what's consuming those hours and how Truyon can give educators their time back.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">8+</div>
          <div className="text-sm text-slate-500">Hours weekly on admin</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">400+</div>
          <div className="text-sm text-slate-500">Hours annually lost</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">70%</div>
          <div className="text-sm text-slate-500">Teachers report burnout</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">The 6 Administrative Tasks Killing Teaching Time</h2>

      {/* Tasks List */}
      {tasks.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="text-xl font-bold text-slate-900">{item.title}</h3>
                <span className="bg-white/80 px-3 py-1 rounded-full text-xs font-bold text-amber-600 border border-amber-200">
                  {item.stat}
                </span>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4 text-sm">{item.description}</p>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>Truyon's Solution</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Impact Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The True Cost of Administrative Burden</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">The Current Reality</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>8+ hours/week on admin (20% of teaching time)</span></li>
              <li className="flex items-start gap-2">• <span>400+ hours/year lost to administrative tasks</span></li>
              <li className="flex items-start gap-2">• <span>70% of teachers report burnout from admin work</span></li>
              <li className="flex items-start gap-2">• <span>Less time for lesson planning and student interaction</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">With Truyon Automation</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>80% reduction in administrative time</span></li>
              <li className="flex items-start gap-2">• <span>50+ hours/week freed for teaching</span></li>
              <li className="flex items-start gap-2">• <span>90% less teacher burnout</span></li>
              <li className="flex items-start gap-2">• <span>More focus on students and pedagogy</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">The Bottom Line</h3>
        <p className="text-indigo-800 leading-relaxed">
          Every hour a teacher spends on administrative work is an hour taken away from students. 
          By automating routine tasks, Truyon gives educators back their most valuable resource—time. 
          Time to inspire, time to teach, time to make a difference.
        </p>
      </div>
    </>
  );
};

export default TeacherWorkload;